/**
 * OfflineStore - localStorage cache abstraction with LRU eviction and TTL.
 *
 * Keys are namespaced under `lc:cache:` with a short hash of the request path.
 * A separate `lc:cache_index` key maintains an access-time ordered index
 * for LRU eviction when the budget (~4 MB) is exceeded.
 */

const CACHE_PREFIX = 'lc:cache:'
const INDEX_KEY = 'lc:cache_index'
const DEFAULT_BUDGET_BYTES = 4 * 1024 * 1024 // 4 MB
const DEFAULT_TTL_MS = 30 * 60 * 1000 // 30 minutes

export interface CacheEntry<T = unknown> {
  data: T
  cachedAt: number
  path: string
  ttl: number
  sizeBytes: number
}

interface CacheIndexEntry {
  key: string
  accessedAt: number
  sizeBytes: number
}

function cacheKey(path: string): string {
  // Simple hash - FNV-1a 32-bit for speed
  let hash = 0x811c9dc5
  for (let i = 0; i < path.length; i++) {
    hash ^= path.charCodeAt(i)
    hash = (hash * 0x01000193) >>> 0
  }
  return `${CACHE_PREFIX}${hash.toString(36)}`
}

export class OfflineStore {
  private budgetBytes: number

  constructor(budgetBytes: number = DEFAULT_BUDGET_BYTES) {
    this.budgetBytes = budgetBytes
  }

  /** Store a response in the cache. Evicts LRU entries if over budget. */
  setCache<T>(path: string, data: T, ttl: number = DEFAULT_TTL_MS): void {
    const key = cacheKey(path)
    const json = JSON.stringify(data)
    const sizeBytes = json.length * 2 // rough UTF-16 estimate

    const entry: CacheEntry<T> = {
      data,
      cachedAt: Date.now(),
      path,
      ttl,
      sizeBytes,
    }

    // Evict until we have room
    this.evictUntilFits(sizeBytes, key)

    try {
      localStorage.setItem(key, JSON.stringify(entry))
      this.updateIndex(key, sizeBytes)
    } catch {
      // localStorage full even after eviction - clear and retry once
      this.clearCache()
      try {
        localStorage.setItem(key, JSON.stringify(entry))
        this.updateIndex(key, sizeBytes)
      } catch {
        // Give up silently - caching is best-effort
      }
    }
  }

  /** Retrieve a cached response. Returns null if missing or expired. */
  getCache<T>(path: string): CacheEntry<T> | null {
    const key = cacheKey(path)
    const raw = localStorage.getItem(key)
    if (!raw) return null

    try {
      const entry = JSON.parse(raw) as CacheEntry<T>
      const age = Date.now() - entry.cachedAt
      if (age > entry.ttl) {
        // Expired - remove
        localStorage.removeItem(key)
        this.removeFromIndex(key)
        return null
      }

      // Update access time in index
      this.touchIndex(key)
      return entry
    } catch {
      localStorage.removeItem(key)
      this.removeFromIndex(key)
      return null
    }
  }

  /** Get total estimated cache size in bytes. */
  getCacheSize(): number {
    const index = this.getIndex()
    return index.reduce((sum, e) => sum + e.sizeBytes, 0)
  }

  /** Clear all cached data. */
  clearCache(): void {
    const index = this.getIndex()
    for (const entry of index) {
      localStorage.removeItem(entry.key)
    }
    localStorage.removeItem(INDEX_KEY)
  }

  /** Evict the least-recently-used entries until `neededBytes` fit within budget. */
  private evictUntilFits(neededBytes: number, excludeKey?: string): void {
    const index = this.getIndex()
    let currentSize = index.reduce((sum, e) => sum + e.sizeBytes, 0)

    // Sort by accessedAt ascending (oldest first)
    const sorted = [...index].sort((a, b) => a.accessedAt - b.accessedAt)

    for (const entry of sorted) {
      if (currentSize + neededBytes <= this.budgetBytes) break
      if (entry.key === excludeKey) continue

      localStorage.removeItem(entry.key)
      currentSize -= entry.sizeBytes
    }

    // Rebuild index from remaining items
    const remaining = index.filter(
      (e) => localStorage.getItem(e.key) !== null,
    )
    this.saveIndex(remaining)
  }

  // --- Index management ---

  private getIndex(): CacheIndexEntry[] {
    const raw = localStorage.getItem(INDEX_KEY)
    if (!raw) return []
    try {
      return JSON.parse(raw) as CacheIndexEntry[]
    } catch {
      return []
    }
  }

  private saveIndex(index: CacheIndexEntry[]): void {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index))
  }

  private updateIndex(key: string, sizeBytes: number): void {
    const index = this.getIndex().filter((e) => e.key !== key)
    index.push({ key, accessedAt: Date.now(), sizeBytes })
    this.saveIndex(index)
  }

  private removeFromIndex(key: string): void {
    const index = this.getIndex().filter((e) => e.key !== key)
    this.saveIndex(index)
  }

  private touchIndex(key: string): void {
    const index = this.getIndex()
    const entry = index.find((e) => e.key === key)
    if (entry) {
      entry.accessedAt = Date.now()
      this.saveIndex(index)
    }
  }
}
